import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import {
  cityLevelDataAPI,
  dataGuardFiltersEndPoints,
  FETCH_CITY_LEVEL_DATA,
  GET_DATA_GUARD_FILTERS_LIST,
  getDetailsURL,
  locationFilterListAPI,
  SET_CITY_LEVEL_DATA,
  SET_CITY_LEVEL_LOADER,
  SET_DATA_GUARD_LOCATION_FILTERS,
  SET_DATA_GUARD_OWNERS_FILTERS,
  SET_DATA_GUARD_POLICY_SUBTYPE_FILTERS,
  SET_DATA_GUARD_SEVERITY_FILTERS,
  SET_DATA_GUARD_USER_ROLES_FILTERS,
  SET_DATA_GUARD_USERS_STATUS_FILTER_LIST,
  SET_NOTIFICATION_UPDATE_STATUS,
  SET_REVOKE_ACCESS_UPDATE_STATUS,
  SeverityFilterMap,
} from "../../Utility/DataguardConstant";
import { getAxiosHeaders, getService } from "../../../../library/RestAPI";
import { maskUsername } from "../../../SysGuard/SysGuardCommonComp";
import { DETAILS_PAGE_LOADER } from "../../../Data Tag/DataTagConstants";

// Local variable to track the latest request ID
let currentRequestId = null;

export function* getDataGuardFiltersList(action) {
  try {
    const fromTimeStamp = yield select(
      (state) => state.DataguardReducer.fromTimeStamp
    );
    const toTimeStamp = yield select(
      (state) => state.DataguardReducer.toTimeStamp
    );

    const { filterEndpoint, filterType } = action.payload;

    const options = {
      method: "GET",
      url:
        filterType === "locations"
          ? locationFilterListAPI(fromTimeStamp, toTimeStamp)
          : `${getDetailsURL(filterEndpoint)}`,
      headers: getAxiosHeaders(true),
    };

    const response = yield getService(options);

    if (response && response.status === 200) {
      if (filterType === "locations") {
        yield put({
          type: SET_DATA_GUARD_LOCATION_FILTERS,
          payload: response.data || [],
        });
      }

      if (filterType === "behaviormodels") {
        yield put({
          type: SET_DATA_GUARD_POLICY_SUBTYPE_FILTERS,
          payload: response.data || [],
        });
        yield put({
          type: SET_DATA_GUARD_SEVERITY_FILTERS,
          payload: ["HIGH", "MEDIUM", "LOW"],
        });
      }

      if (filterType === "roles") {
        yield put({
          type: SET_DATA_GUARD_USER_ROLES_FILTERS,
          payload: response.data || [],
        });
      }

      if (filterType === "users") {
        yield put({
          type: SET_DATA_GUARD_USERS_STATUS_FILTER_LIST,
          payload: response.data || [],
        });
      }

      if (filterType === "publicdata") {
        yield put({
          type: SET_DATA_GUARD_OWNERS_FILTERS,
          payload: response.data || [],
        });
      }
    } else {
      console.error("Error fetching filter data");
    }
  } catch (error) {
    console.error("Error fetching filter data:", error);
  }
}

function* fetchDetailPageDataSaga(action) {
  try {
    const {
      pageType,
      apiEndpoint,
      fromTimeStamp,
      toTimeStamp,
      page,
      rowsPerPage,
      locationFilters = [],
      policySubtypeFilters = [],
      severityFilters = [],
      userRolesFilters = [],
      ownersFilters = [],
      userNamesFilters=[],
      usersStatusFilters = [],
      filterType,
    } = action.payload;

    // Generate and store unique request ID for this fetch
    const requestId = `${pageType}_${apiEndpoint}_${Date.now()}`;
    currentRequestId = requestId;

    const notificationStatus = yield select(
      (state) => state.DataguardReducer.notificationUpdateStatus
    );
    const revokeAccessUpdateStatus = yield select(
      (state) => state.DataguardReducer.revokeAccessUpdateStatus
    );

    const joinFilters = (filters) =>
      Array.isArray(filters) && filters.length > 0 ? filters.join(",") : "";

    const paramData = {};


    if (filterType === "locations" && locationFilters.length > 0) {
      paramData.countries = joinFilters(locationFilters);
    }

    if (filterType === "behaviormodels") {
      if (policySubtypeFilters.length > 0) {
        paramData.policies = joinFilters(policySubtypeFilters);
      }
      if (severityFilters.length > 0) {
        paramData.severities = severityFilters
          .map((s) => s.toLowerCase())
          .join(",");
      }
    }

    if (filterType === "users" && userNamesFilters.length > 0) {
      paramData.userNames = joinFilters(userNamesFilters);
    }

    if (filterType === "users" && usersStatusFilters.length > 0) {
      paramData.userStatus = joinFilters(usersStatusFilters);
    }


    if (filterType === "roles" && userRolesFilters.length > 0) {
      paramData.roleNames = joinFilters(userRolesFilters);
    }

    if (filterType === "publicdata" && ownersFilters.length > 0) {
      paramData.owners = joinFilters(ownersFilters);
    }

    yield put({ type: "FETCH_DETAIL_PAGE_DATA_START" });

    const url = `${getDetailsURL(
      apiEndpoint
    )}?fromTimeStamp=${fromTimeStamp}&toTimeStamp=${toTimeStamp}&size=${rowsPerPage}&page=${page}`;

    const response = yield getService({
      method: "GET",
      url,
      headers: getAxiosHeaders(true),
      params: paramData,
    });


    if (response?.status === 200) {
      let responseData = response.data;

      // Only for Users page
      if (pageType === "users" && Array.isArray(responseData.users)) {
        responseData = {
          ...responseData,
          users: responseData.users.map((user) => ({
            ...user,
            dataSource: {
              id: user.dataSourceId ?? null,
              name: user.dataSourceName ?? "-",
              type: user.dataSourceType ?? "-",
            },
          })),
        };
      }

      // Verify this response is still relevant (takeLatest cancels saga but API call may complete)
      const currentPageType = yield select(
        (state) => state.DetailPageReducer?.lastPageType
      );
      const currentApiEndpoint = yield select(
        (state) => state.DetailPageReducer?.lastApiEndpoint
      );

      // Triple validation: page context + request ID to prevent race conditions
      if (
        currentPageType === pageType && 
        currentApiEndpoint === apiEndpoint && 
        currentRequestId === requestId
      ) {
        yield put({
          type: "FETCH_DETAIL_PAGE_DATA_SUCCESS",
          payload: { pageType, data: responseData || [] },
        });
      } else {
        // Silently ignore stale responses
        console.log(
          `Ignoring stale response - pageType: ${pageType} (current: ${currentPageType}), ` +
          `requestId: ${requestId} (current: ${currentRequestId})`
        );
      }

      if (notificationStatus)
        yield put({ type: SET_NOTIFICATION_UPDATE_STATUS, payload: false });

      if (revokeAccessUpdateStatus)
        yield put({ type: SET_REVOKE_ACCESS_UPDATE_STATUS, payload: false });
    } else {
      yield put({
        type: "FETCH_DETAIL_PAGE_DATA_FAILURE",
        payload: "API call failed",
      });
    }
  } catch (error) {
    yield put({
      type: "FETCH_DETAIL_PAGE_DATA_FAILURE",
      payload: error.message,
    });
  }
}

function* fetchCityLevelData(action) {
  yield put({ type: DETAILS_PAGE_LOADER, payload: true });
  const fromTimeStamp = yield select((state) => state.DataguardReducer.fromTimeStamp);
  const toTimeStamp = yield select((state) => state.DataguardReducer.toTimeStamp);
  const { country, page, size } = action.payload;
  const options = {
    method: "GET",
    url: cityLevelDataAPI(fromTimeStamp, toTimeStamp, country, page, size),
    headers: getAxiosHeaders(true),
  };
  try {
    const response = yield getService(options);
    if (response && response.status === 200) {
      const data = response?.data;
      yield put({ type: SET_CITY_LEVEL_DATA, payload: { country, data } });
      yield put({ type: DETAILS_PAGE_LOADER, payload: false });
    } else {
      console.log("Something went wrong, Please try again");
      yield put({ type: DETAILS_PAGE_LOADER, payload: false });
    }
  } catch (err) {
    console.log(err);
    yield put({ type: DETAILS_PAGE_LOADER, payload: false });
  }
}
export default function* DetailPageSaga() {
  yield takeLatest("FETCH_DETAIL_PAGE_DATA_REQUEST", fetchDetailPageDataSaga);
  yield takeEvery(GET_DATA_GUARD_FILTERS_LIST, getDataGuardFiltersList);
  yield takeEvery(FETCH_CITY_LEVEL_DATA, fetchCityLevelData);

}